import { formatDate } from '@angular/common';
import { Component, EventEmitter, Injector, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import {
  ChatChannelTypeEnum,
  ChatMessage,
  ChatMessageTypeEnum,
  ChatService,
  ChatThreadListItem,
  ChatUserDisplayMap,
} from 'lib-trend-core';
import { AbstractComponent } from 'lib-trend-core';
import { takeUntil } from 'rxjs/operators';
import { chatFileIcon } from '../../chat-composer/chat-attachment.util';
import { ChatMentionRender } from '../../pipes/chat-markdown.renderer';
import { resolveChatInitials } from '../../chat-initials.util';

/** Jump target emitted to the workspace (opens + highlights the thread root). */
export interface ChatThreadsJump {
  channelId: string;
  messageId: string;
}

const THREADS_PAGE_SIZE = 10;

/** One rendered message (root or reply preview) inside a thread card. */
interface ChatThreadsMessageRow {
  authorName: string;
  authorInitial: string;
  authorImage?: string;
  timeLabel: string;
  deleted: boolean;
  isMedia: boolean;
  content: string;
  fileName?: string;
  fileIcon?: string;
  mentionRender: ChatMentionRender[];
}

/** One thread card: source conversation + root + latest replies. */
interface ChatThreadsRow {
  rootId: string;
  channelId: string;
  channelName: string;
  channelIcon: string;
  root: ChatThreadsMessageRow;
  replies: ChatThreadsMessageRow[];
  replyCountLabel: string;
}

/**
 * Internal Chat "Mensagens de Tópico" pane (DEV-1984) — Slack-style Threads
 * view: every thread the user participates in (rooted or replied), newest
 * activity first, listed as cards carrying the source conversation, the root
 * message and the latest replies. Clicking jumps into the conversation with
 * the root highlighted — the full thread panel is one click away there.
 *
 * Reads `GET /api/chat/threads` directly (no store slice — the list is
 * ephemeral view state, like the channel directory) and appends pages with
 * the endpoint's `hasMore` meta.
 */
@Component({
  selector: 'chat-threads',
  templateUrl: './chat-threads.component.html',
  styleUrls: ['./chat-threads.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatThreadsComponent extends AbstractComponent implements OnInit {

  /** Row clicked — the workspace opens the conversation at the thread root. */
  @Output() jump = new EventEmitter<ChatThreadsJump>();

  rows: ChatThreadsRow[] = [];
  hasMore = false;
  loadFailed = false;
  loadingMore = false;

  private page = 1;

  constructor(
    injector: Injector,
    private readonly chatService: ChatService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.load(1, false);
  }

  reload(): void {
    this.load(1, false);
  }

  loadMore(): void {
    if (!this.hasMore || this.loadingMore) return;
    this.load(this.page + 1, true);
  }

  open(row: ChatThreadsRow): void {
    this.jump.emit({ channelId: row.channelId, messageId: row.rootId });
  }

  trackRow(_index: number, row: ChatThreadsRow): string {
    return row.rootId;
  }

  private load(page: number, append: boolean): void {
    if (append) {
      this.loadingMore = true;
    } else {
      this.loading = true;
    }
    this.loadFailed = false;

    this.chatService.getThreads({ page, perPage: THREADS_PAGE_SIZE })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (envelope) => {
          this.loading = false;
          this.loadingMore = false;
          this.page = page;
          this.hasMore = envelope?.meta?.hasMore === true;
          const userDisplay = envelope?.meta?.userDisplay ?? {};
          const incoming = (envelope?.data ?? []).map((item) => this.buildRow(item, userDisplay));
          this.rows = append ? [...this.rows, ...incoming] : incoming;
        },
        error: () => {
          this.loading = false;
          this.loadingMore = false;
          this.loadFailed = true;
        },
      });
  }

  private buildRow(item: ChatThreadListItem, userDisplay: ChatUserDisplayMap): ChatThreadsRow {
    const replyCount = item.root?.threadCount ?? item.replies.length;
    return {
      rootId: item.root._id,
      channelId: item.channel?._id ?? String(item.root.channel),
      channelName: item.channel?.name || this.t('chat.bubble.channelConversation'),
      channelIcon: this.channelIcon(item.channel?.type),
      root: this.buildMessageRow(item.root, userDisplay),
      replies: item.replies.map((reply) => this.buildMessageRow(reply, userDisplay)),
      replyCountLabel: replyCount === 1
        ? this.t('chat.threadsView.replyCountOne')
        : this.t('chat.threadsView.replyCount', { count: replyCount }),
    };
  }

  private buildMessageRow(message: ChatMessage, userDisplay: ChatUserDisplayMap): ChatThreadsMessageRow {
    const authorName = userDisplay[message.sender]?.name ?? this.t('chat.thread.unknownAuthor');
    const isMedia = message.type !== ChatMessageTypeEnum.TEXT;
    const currentUserId = this.getIDCurrentUser();

    return {
      authorName,
      authorInitial: resolveChatInitials(authorName || '?'),
      authorImage: userDisplay[message.sender]?.image,
      timeLabel: formatDate(new Date(message.createdAt), 'dd/MM HH:mm', 'en-US'),
      deleted: message.deleted === true,
      isMedia,
      content: message.content ?? '',
      fileName: message.fileName,
      fileIcon: isMedia ? chatFileIcon(message.contentType, message.fileName) : undefined,
      mentionRender: (message.mentions ?? [])
        .map((id) => ({
          name: userDisplay[id]?.name ?? '',
          self: id === currentUserId,
        }))
        .filter((mention) => !!mention.name),
    };
  }

  private channelIcon(type?: ChatChannelTypeEnum): string {
    switch (type) {
      case ChatChannelTypeEnum.PUBLIC: return 'ri-hashtag';
      case ChatChannelTypeEnum.PRIVATE: return 'ri-lock-line';
      case ChatChannelTypeEnum.GROUP: return 'ri-group-line';
      case ChatChannelTypeEnum.DIRECT: return 'ri-chat-1-line';
      default: return 'ri-hashtag';
    }
  }
}
